import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import type { editor } from 'monaco-editor';
import MonacoEditor from 'react-monaco-editor';
import { useTranslation } from 'react-i18next';
import { RuntimeConfigurationValueType } from '../../../types/enums/runtime-configuration-enums';
import {
    coerceBooleanValue,
    isBooleanValueType,
    isJsonValueType,
    JsonValidationResult,
    normalizeValueType,
    validateJSON,
    valueToDisplayString,
} from '../runtimeConfiguration.helpers';
import './RuntimeConfigurationValueEditor.scss';

type Props = {
    valueType: RuntimeConfigurationValueType | string;
    value: unknown;
    readOnly?: boolean;
    onChange: (value: unknown) => void;
    onValidationChange?: (result: JsonValidationResult) => void;
};

type CodeEditorProps = {
    language: 'json' | 'plaintext';
    value: string;
    readOnly?: boolean;
    onChange: (text: string) => void;
};

const BOOLEAN_OPTIONS = [
    { value: 'true', labelKey: 'runtimeConfiguration.values.yes' },
    { value: 'false', labelKey: 'runtimeConfiguration.values.no' },
];

const MIN_EDITOR_HEIGHT = 280;
const LINE_HEIGHT = 20;

const toJsonEditorText = (value: unknown): string => {
    try {
        if (typeof value === 'string') {
            const trimmed = value.trim();
            if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
                return JSON.stringify(JSON.parse(trimmed), null, 2);
            }
            return value;
        }
        return JSON.stringify(value ?? {}, null, 2);
    } catch {
        return typeof value === 'string' ? value : '{}';
    }
};

const toCodeEditorText = (value: unknown, valueType: RuntimeConfigurationValueType | string | number): string => {
    if (isJsonValueType(valueType)) {
        return toJsonEditorText(value);
    }
    return valueToDisplayString(value);
};

const RuntimeConfigurationCodeEditor: FC<CodeEditorProps> = ({ language, value, readOnly, onChange }) => {
    const [height, setHeight] = useState(MIN_EDITOR_HEIGHT);
    const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);

    const layoutEditor = useCallback((editor: editor.IStandaloneCodeEditor) => {
        const contentHeight = editor.getContentHeight();
        const nextHeight = Math.max(contentHeight, MIN_EDITOR_HEIGHT);
        setHeight(nextHeight);
        editor.layout({
            width: editor.getLayoutInfo().width,
            height: nextHeight,
        });
    }, []);

    useEffect(() => {
        if (editorRef.current) {
            layoutEditor(editorRef.current);
        }
    }, [value, layoutEditor]);

    const handleEditorDidMount = (editor: editor.IStandaloneCodeEditor) => {
        editorRef.current = editor;
        layoutEditor(editor);
        editor.onDidContentSizeChange(() => layoutEditor(editor));
    };

    const handleChange = (text = '') => {
        onChange(text);
    };

    return (
        <Box className="runtime-config-editor__monaco" style={{ height: `${height}px` }}>
            <MonacoEditor
                height={`${height}px`}
                language={language}
                theme="vs-light"
                value={value}
                editorDidMount={handleEditorDidMount}
                options={{
                    readOnly,
                    minimap: { enabled: false },
                    scrollBeyondLastLine: false,
                    automaticLayout: true,
                    lineNumbers: 'on',
                    wordWrap: 'on',
                    wrappingIndent: 'indent',
                    fontSize: 13,
                    lineHeight: LINE_HEIGHT,
                    fontFamily: "'JetBrains Mono', 'Consolas', 'Courier New', monospace",
                    padding: { top: 12, bottom: 12 },
                    renderLineHighlight: readOnly ? 'none' : 'line',
                    scrollbar: {
                        vertical: 'hidden',
                        horizontal: 'auto',
                        verticalScrollbarSize: 0,
                        alwaysConsumeMouseWheel: false,
                    },
                    overviewRulerLanes: 0,
                    hideCursorInOverviewRuler: true,
                    folding: language === 'json',
                    tabSize: 2,
                    insertSpaces: true,
                }}
                onChange={handleChange}
            />
        </Box>
    );
};

export const RuntimeConfigurationValueEditor: FC<Props> = ({
    valueType,
    value,
    readOnly = false,
    onChange,
    onValidationChange,
}) => {
    const { t } = useTranslation();
    const normalizedValueType = normalizeValueType(valueType);
    const isBoolean = isBooleanValueType(normalizedValueType);
    const isJson = isJsonValueType(normalizedValueType);
    const editorLanguage: 'json' | 'plaintext' = isJson ? 'json' : 'plaintext';
    const boolSelected = coerceBooleanValue(value) ? 'true' : 'false';
    const [codeDraft, setCodeDraft] = useState(() => toCodeEditorText(value, normalizedValueType));
    const jsonValidation = isJson ? validateJSON(codeDraft) : { valid: true, error: null };

    useEffect(() => {
        if (!isBoolean) {
            setCodeDraft(toCodeEditorText(value, normalizedValueType));
        }
    }, [value, normalizedValueType, isBoolean]);

    useEffect(() => {
        if (!isJson) {
            onValidationChange?.({ valid: true, error: null });
            return;
        }
        onValidationChange?.(validateJSON(codeDraft));
    }, [codeDraft, isJson, onValidationChange]);

    const commitCodeDraft = (text: string) => {
        setCodeDraft(text);
        if (readOnly) {
            return;
        }
        if (isJson) {
            try {
                onChange(JSON.parse(text));
            } catch {
                onChange(text);
            }
            return;
        }
        onChange(text);
    };

    return (
        <Box className="runtime-config-editor">
            <Typography variant="caption" className="runtime-config-editor__label">
                {t('runtimeConfiguration.detail.valueSection')}
            </Typography>

            {isBoolean && (
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={boolSelected}
                    className="runtime-config-editor__segment"
                    onChange={(_, next) => {
                        if (!readOnly && next != null) {
                            onChange(next === 'true');
                        }
                    }}
                >
                    {BOOLEAN_OPTIONS.map((option) => (
                        <ToggleButton
                            key={option.value}
                            value={option.value}
                            className="runtime-config-editor__segment-btn"
                            disabled={readOnly}
                        >
                            {t(option.labelKey)}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            )}

            {!isBoolean && (
                <>
                    <Box
                        className={`runtime-config-editor__monaco-wrap${
                            isJson && !jsonValidation.valid ? ' runtime-config-editor__monaco-wrap--invalid' : ''
                        }`}
                    >
                        <RuntimeConfigurationCodeEditor
                            language={editorLanguage}
                            value={codeDraft}
                            readOnly={readOnly}
                            onChange={commitCodeDraft}
                        />
                    </Box>
                    {isJson && !jsonValidation.valid && (
                        <Alert severity="error" className="runtime-config-editor__json-error">
                            <Typography variant="body2" component="span" display="block">
                                {t('runtimeConfiguration.detail.jsonPrettifyInvalid')}
                            </Typography>
                            {jsonValidation.error && (
                                <Typography variant="caption" component="span" display="block" sx={{ mt: 0.5 }}>
                                    {jsonValidation.error}
                                </Typography>
                            )}
                        </Alert>
                    )}
                </>
            )}
        </Box>
    );
};
